import { useState } from 'react';

import cn from 'classnames';
import { MESSAGE_STATE_ERROR, MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import { Permissions } from 'constants/permissions';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { useAccessControl } from 'hooks/access-control';
import { Badge } from 'react-bootstrap';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Moment from 'react-moment';
import { useSelector } from 'react-redux';

import { ConfirmationModal } from 'components/ConfirmationModal/ConfirmationModal';
import { Button } from 'components/FormComponents';
import { IconButton } from 'components/IconButton/IconButton';
import { Table } from 'components/Table/Table';

import { useInviteUserMutation, useResendUserInviteEmailMutation } from 'api/user-api';
import { useDisable2FAMutation } from 'api/user-api';
import { useDisableUserMutation, useEnableUserMutation } from 'api/user-api';
import { selectPermissions } from 'api/user-permissions-slice';

import { NewUserModal } from '../../components/NewUserModal/NewUserModal';
import styles from './UsersTab.module.scss';

export const UsersTab = ({
  company,
  roles,
  teams,
  getCompanyUsers = () => {},
  messagePopUp = () => {},
  setLoading = () => {},
  deleteUser = () => {},
  updateCompanyOwner = () => {},
}) => {
  const permissions = useSelector(selectPermissions);
  const hasAccess = useAccessControl(permissions);

  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    records_per_page: 15,
    total_pages: 1,
    total_records: 0,
  });

  const [inviteUser] = useInviteUserMutation();
  const [resendUserInviteEmail] = useResendUserInviteEmailMutation();
  const [disable2FA] = useDisable2FAMutation();
  const [disableUser] = useDisableUserMutation();
  const [enableUser] = useEnableUserMutation();

  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [showDisableMFAModal, setShowDisableMFAModal] = useState(false);

  const [userToSetAsAdmin, setUserToSetAsAdmin] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToDisable, setUserToDisable] = useState(null);
  const [didableSSOModal, setDidableSSOModal] = useState(false);

  const fetchUsers = async (params = {}) => {
    setLoading(true);
    const { data } = await getCompanyUsers({ id: company.id, params });
    setPagination(data.result.pagination);
    setUsers(data.result.users);
    setLoading(false);
  };

  const handleNewUser = async userData => {
    setShowNewUserModal(false);
    setLoading(true);
    const { data: response } = await inviteUser(userData);
    if (response.result) {
      setUsers(curr => [...curr, response.result]);
      messagePopUp({
        text: `User ${userData.first_name} was successfully invited`,
        state: MESSAGE_STATE_SUCCESS,
        hide: true,
      });
    } else {
      messagePopUp({
        text: `Email ${userData.email} it's being use by another user`,
        state: MESSAGE_STATE_ERROR,
        hide: true,
      });
    }
    setLoading(false);
  };

  const setCompanyOwner = async () => {
    setLoading(true);
    await updateCompanyOwner({ id: company.id, userId: userToSetAsAdmin.id });
    setUserToSetAsAdmin(false);
    setLoading(false);
  };

  const handleUserDeletion = async () => {
    setLoading(true);
    const { data: result } = await deleteUser(userToDelete.id);
    setUsers(users.filter(u => u.id !== userToDelete.id));
    messagePopUp({
      text: `${userToDelete.name} Deleted. ${result} candidates reassigned`,
      state: MESSAGE_STATE_SUCCESS,
      hide: true,
    });
    setUserToDelete(null);
    setLoading(false);
  };

  const handleDisableMFAModal = async () => {
    setLoading(true);
    await disable2FA(showDisableMFAModal.id);
    setUsers(
      users.map(user =>
        user.id === showDisableMFAModal.id ? { ...user, two_factor_auth_enabled: false } : user,
      ),
    );
    setShowDisableMFAModal(false);
    setLoading(false);
  };

  const handleDisableSSO = async () => {
    setLoading(true);
    await disableSSOOrganization();
    setDidableSSOModal(null);
    setLoading(false);
  };

  const handleResendUserInviteEmail = async user => {
    setLoading(true);
    await resendUserInviteEmail(user.id);
    messagePopUp({
      text: `Invite re-sent for ${user.name}`,
      state: MESSAGE_STATE_SUCCESS,
      hide: true,
    });
    setLoading(false);
  };

  const handleDisableUser = async () => {
    setLoading(true);
    await disableUser(userToDisable.id);
    setUsers(
      users.map(user =>
        user.id === userToDisable.id ? { ...user, disabled_at: new Date().toISOString() } : user,
      ),
    );
    setUserToDisable(null);
    setLoading(false);
  };

  const handleEnableUser = async user => {
    setLoading(true);
    await enableUser(user.id);
    setUsers(users.map(u => (u.id === user.id ? { ...u, disabled_at: null } : u)));
    setLoading(false);
  };

  return (
    <div>
      <ConfirmationModal
        visible={!!userToSetAsAdmin}
        title={`Set ${userToSetAsAdmin?.name} as company owner`}
        subtitle={`Are you sure you want to set ${userToSetAsAdmin?.name} as Company owner? This will make them the main user for the company.`}
        onClose={() => {
          setUserToSetAsAdmin(null);
        }}
        onConfirmation={setCompanyOwner}
        buttonTitle="Set as owner"
      />

      <ConfirmationModal
        visible={!!showDisableMFAModal}
        title={`Disable MFA for ${showDisableMFAModal?.name}`}
        subtitle={`Are you sure you want to disable MFA for ${showDisableMFAModal?.name}?`}
        onClose={() => {
          setShowDisableMFAModal(false);
        }}
        onConfirmation={handleDisableMFAModal}
        buttonTitle="Disable MFA"
      />

      <ConfirmationModal
        visible={!!userToDelete}
        title={`Delete ${userToDelete?.name}`}
        subtitle={
          <span>
            Are you sure you want to delete <b>{userToDelete?.name}</b> as user? <br />
            The user won't longer have access to Checkmate. We will also transfer all it's
            candidates to the company administrator. <br />
            <b>This cannot be undone.</b>
          </span>
        }
        onClose={() => {
          setUserToDelete(null);
        }}
        confirmationKey={userToDelete?.email}
        onConfirmation={handleUserDeletion}
        buttonTitle="Delete user"
        dataTestId="delete-user-confirmation"
      />

      <ConfirmationModal
        visible={!!userToDisable}
        title={`Disable ${userToDisable?.name}`}
        subtitle={
          <span>
            Are you sure you want to disable <b>{userToDisable?.name}</b>? They will immediately
            lose access to Checkmate until you re-enable them.
          </span>
        }
        onClose={() => {
          setUserToDisable(null);
        }}
        onConfirmation={handleDisableUser}
        buttonTitle="Disable user"
      />

      <ConfirmationModal
        visible={didableSSOModal}
        title={`Disable Single Sign On`}
        subtitle={
          <span>
            <br></br>
            Are you sure you want to disable this SSO?
          </span>
        }
        onClose={() => {
          setDidableSSOModal(null);
        }}
        onConfirmation={handleDisableSSO}
        buttonTitle="Disable"
      />

      <NewUserModal
        roles={roles}
        teams={teams}
        visible={showNewUserModal}
        onSubmit={handleNewUser}
        onClose={() => setShowNewUserModal(false)}
      />

      <Table.Context onQueryUpdate={fetchUsers} defaultQuery={{ page: 1, search: '' }}>
        <Table.TableContent>
          <Table.Filters>
            <Table.LeftFilters>
              <Table.Search placeholder="Search for a user..." dataTestId="users-search-input" />
            </Table.LeftFilters>
            <Table.RightFilters>
              {(!company.ad_sync_directory_id || company.ad_sync_allow_manual_user_creation) && (
                <Button
                  icon="user-plus"
                  dataTestId="users-invite-button"
                  onClick={() => setShowNewUserModal(true)}>
                  Invite
                </Button>
              )}
            </Table.RightFilters>
          </Table.Filters>
          <Table className="u-margin-top--large">
            <Table.Head>
              <Table.Row>
                <Table.Header sortKey="users.first_name">Name</Table.Header>
                <Table.Header sortKey="users.email">Email</Table.Header>
                <Table.Header>Teams</Table.Header>
                <Table.Header>Roles</Table.Header>
                <Table.Header>MFA</Table.Header>
                <Table.Header>Verification</Table.Header>
                <Table.Header>Status</Table.Header>
                <Table.Header>Last Login</Table.Header>
                <Table.Header></Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {users.map(user => (
                <Table.Row key={user.id} dataTestId="users-table-row">
                  <Table.Col styles={{ 'vertical-align': 'center' }}>
                    <div className={styles.userNameColumn}>
                      <AdminUserToggle
                        company={company}
                        user={user}
                        onClick={setUserToSetAsAdmin}
                      />{' '}
                      {user.name}
                      {user.directory_managed && (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Managed by Active Directory sync</Tooltip>}>
                          <Badge className="u-status-in-progress u-margin-left--small">
                            Synced
                          </Badge>
                        </OverlayTrigger>
                      )}
                    </div>
                  </Table.Col>
                  <Table.Col>{user.email}</Table.Col>
                  <Table.Col>
                    {user.teams?.map(team => (
                      <span className={styles.roleBadge} key={team}>
                        {team}
                      </span>
                    ))}
                  </Table.Col>
                  <Table.Col>
                    {user.roles?.map(role => (
                      <span className={styles.roleBadge} key={role}>
                        {role}
                      </span>
                    ))}
                  </Table.Col>
                  <Table.Col>
                    {hasAccess(Permissions.Users2faWrite) && user.two_factor_auth_enabled ? (
                      <OverlayTrigger placement="top" overlay={<Tooltip>Disable MFA</Tooltip>}>
                        <Badge
                          className="u-status-completed u-pointer"
                          onClick={() => setShowDisableMFAModal(user)}>
                          Enabled
                        </Badge>
                      </OverlayTrigger>
                    ) : user.two_factor_auth_enabled ? (
                      <Badge className="u-status-completed">Enabled</Badge>
                    ) : (
                      <Badge className="u-status-canceled">Disabled</Badge>
                    )}
                  </Table.Col>
                  <Table.Col>
                    <Badge
                      data-testid="user-verification-badge"
                      className={user.verified ? 'u-status-completed' : 'u-status-pending'}>
                      {user.verified ? 'Verified' : 'Pending'}
                    </Badge>
                  </Table.Col>
                  <Table.Col>
                    <Badge
                      data-testid="user-status-badge"
                      className={user.disabled_at ? 'u-status-overdue' : 'u-status-completed'}>
                      {user.disabled_at ? 'Disabled' : 'Active'}
                    </Badge>
                  </Table.Col>
                  <Table.Col>
                    {user.last_login && (
                      <Moment fromNow utc>
                        {user.last_login}
                      </Moment>
                    )}
                  </Table.Col>
                  <Table.Col align="right">
                    {!user.verified && (
                      <IconButton
                        icon="send"
                        onClick={() => handleResendUserInviteEmail(user)}
                        tip="Resend verification email"
                      />
                    )}
                    {user.id !== company.creator_id &&
                      (user.disabled_at ? (
                        <IconButton
                          icon="user-check"
                          className="u-margin-left--small"
                          onClick={() => handleEnableUser(user)}
                          tip={`Enable ${user.name}`}
                        />
                      ) : (
                        <IconButton
                          icon="user-x"
                          className="u-margin-left--small"
                          onClick={() => setUserToDisable(user)}
                          tip={`Disable ${user.name}`}
                        />
                      ))}
                    {user.id !== company.creator_id && (
                      <IconButton
                        icon="trash-2"
                        dataTestId="user-delete-button"
                        className="u-margin-left--small"
                        disabled={user.directory_managed}
                        onClick={() => setUserToDelete(user)}
                        tip={
                          user.directory_managed
                            ? 'Managed by Active Directory sync — remove them from the directory instead'
                            : `Delete ${user.name}`
                        }
                      />
                    )}
                  </Table.Col>
                </Table.Row>
              ))}
            </Table.Body>
            <Table.Pagination {...pagination} />
          </Table>
        </Table.TableContent>
      </Table.Context>
    </div>
  );
};

const AdminUserToggle = ({ company, user, onClick = () => {} }) => {
  const isCompanyCreator = company.creator_id === user.id;

  const handleClick = () => {
    if (isCompanyCreator) return;

    onClick(user);
  };

  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{isCompanyCreator ? 'Company owner' : 'Set as company owner'}</Tooltip>}>
      <div>
        <FeatherIcon
          size={17}
          className={cn(styles.starIcon, { [styles.selected]: company.creator_id === user.id })}
          icon="star"
          onClick={handleClick}
        />
      </div>
    </OverlayTrigger>
  );
};
